import React, { useCallback, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { SoftwareAdminApi, SoftwareStatus } from 'openapi'
import { Skeleton } from 'shadcdn/ui/skeleton'
import { Badge } from 'ui/Badge/Badge'
import { Tabs, TabsList, TabsTrigger } from 'shadcdn/ui/tabs'
import { DebouncedInput } from 'ui/DebouncedInput/DebouncedInput'

import { AdminSoftwareListPath, AdminSofwarePagePath } from '../routes'

import { AdminSoftwareTable } from './components/AdminSoftwareTable'
import { AddSoftwareButton } from './components/AddSoftwareButton'
import { AdminSoftwarePagination } from './components/AdminSoftwarePagination'
import { useSoftwareAdminList } from './hooks'

type Props = {}

export const AdminSoftwareListPage = React.memo((_props: Props) => {
  const { status } = useParams()
  const [ordering, setOrdering] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('search') ?? ''

  const PER_PAGE = 50
  const [page, setPage] = useState<number>(0)
  const { data, isFetching } = useSoftwareAdminList({
    limit: PER_PAGE,
    offset: page * PER_PAGE,
    status: status as SoftwareStatus,
    query: search,
    ordering,
  })
  const softwareList = data?.items ?? []
  const pagesCount = Math.ceil((data?.count ?? 0) / PER_PAGE) ?? 0

  const navigate = useNavigate()
  const navigateSoftware = useCallback(
    (softwareId: number | 'new') => navigate(AdminSofwarePagePath({ softwareId })),
    [navigate]
  )

  const statusesWithAmountResult = useQuery({
    queryKey: ['SoftwareAdminApi.statuses'],
    queryFn: () => SoftwareAdminApi.statuses(),
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const statusesWithAmount: Array<[SoftwareStatus | undefined, number]> = useMemo(() => {
    const data: Record<string, number> = statusesWithAmountResult.data as any
    if (!data) return []
    const retVal: Array<[SoftwareStatus | undefined, number]> = []

    retVal.push([undefined, Object.values(data).reduce((acc, val) => acc + val, 0)])
    for (const status of Object.values(SoftwareStatus)) {
      if (data[status]) {
        retVal.push([status, data[status]])
      }
    }
    return retVal
  }, [statusesWithAmountResult.data])

  // prettier-ignore
  const filteredSoftware = (!status || status === 'all')
    ? softwareList
    : softwareList.filter((software) => software.status === status)

  return (
    <div className="mx-2">
      <div className="flex gap-x-4">
        <div className="flex flex-grow gap-2 mb-1">
          <Tabs defaultValue={status} onValueChange={(val: any) => navigate(AdminSoftwareListPath({ status: val }))}>
            <TabsList className="bg-gray-200">
              {statusesWithAmount.map(([_status, amount]) => (
                <TabsTrigger key={String(_status)} value={_status!}>
                  <span className="capitalize">{_status ?? 'All'}</span>
                  <Badge className="ml-2" color={_status === status ? 'gray' : 'white'}>
                    {amount}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div>
          <AddSoftwareButton navigateSoftware={navigateSoftware} />
        </div>

        <DebouncedInput
          autoFocus={true}
          debounceTime={300}
          value={search}
          onChange={(newSearch: string = '') => {
            if (newSearch === search) return
            setSearchParams({ search: newSearch })
          }}
          placeholder="Filter..."
          className="py-1 px-2 text-sm mt-1 w-80"
        />
      </div>
      {data && !isFetching ? (
        <>
          <div className="bg-white p-4 rounded-md shadow-md">
            <AdminSoftwareTable
              softwareList={filteredSoftware}
              ordering={ordering}
              setOrdering={setOrdering}
              highlighQuery={search}
            />
          </div>

          <AdminSoftwarePagination
            className="mt-4 [&_a]:text-xs [&_a]:p-1 [&_a]:h-7"
            page={page}
            setPage={setPage}
            pagesCount={pagesCount}
          />
        </>
      ) : (
        <div>
          <Skeleton className="h-14 w-full my-1" />
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton className="h-10 w-full my-1" key={i} />
          ))}
        </div>
      )}
    </div>
  )
})
